import * as React from "react";
import { TrackingDebugPanel } from "@base/lib/tracking-lib/debug/TrackingDebugPanel";
import { browserLocalStorage } from "@base/browserLocalStorage";
import {
  DEV_TOOLS,
  exitDevMode,
  setDevTool,
  setForcedH,
  setDevToolbarCollapsed,
  subscribeDevTools,
  isDevToolOn,
  useDevMode,
  useDevToolbarCollapsed,
  useForcedH,
} from "./devTools";

const GREEN = "#00FF41";
export const DEV_TOOLBAR_HEIGHT = 28;

/**
 * The quiz dev toolbar: a thin bar fixed to the bottom of every page while dev
 * mode is on (`?devmode=true`, which also allows test cards at checkout).
 * Same look as the web app's DevToolbar. Each tool in DEV_TOOLS gets a toggle;
 * tools that are panels (tracking) render here, others (quiz slides) render
 * where they belong and read `useDevTool`.
 *
 * Plain inline styles on purpose: it must look the same on every brand theme.
 */
export function DevToolbar() {
  const devMode = useDevMode();
  const collapsed = useDevToolbarCollapsed();
  // One subscription re-renders all toggles on any tool change.
  const toolStates = React.useSyncExternalStore(
    subscribeDevTools,
    () => DEV_TOOLS.map((tool) => (isDevToolOn(tool.id) ? "1" : "0")).join(""),
    () => "",
  );

  if (!devMode) {
    return null;
  }

  if (collapsed) {
    return (
      <>
        <TrackingDebugPanel />
        <button
          style={{ ...styles.link, ...styles.tab }}
          onClick={() => setDevToolbarCollapsed(false)}
          title="Open the dev toolbar"
        >
          DEV ▴
        </button>
      </>
    );
  }

  return (
    <>
      <TrackingDebugPanel />
      {/* Keeps the end of the page reachable above the fixed bar. */}
      <div style={{ height: DEV_TOOLBAR_HEIGHT }} />
      <div style={styles.bar} data-testid="dev-toolbar">
        <button
          style={{ ...styles.link, fontWeight: "bold", letterSpacing: "0.15em" }}
          onClick={() => setDevToolbarCollapsed(true)}
          title="Fold the toolbar (it can cover sticky page buttons)"
        >
          DEV ▾
        </button>
        <span style={{ whiteSpace: "pre" }} title="Dev mode allows test cards at checkout">
          test cards ✓
        </span>
        {DEV_TOOLS.map((tool, i) => {
          const on = toolStates[i] === "1";
          return (
            <button
              key={tool.id}
              style={{ ...styles.link, opacity: on ? 1 : 0.6 }}
              onClick={() => setDevTool(tool.id, !on)}
              title={tool.title}
              aria-pressed={on}
            >
              [{on ? "x" : " "}] {tool.label}
            </button>
          );
        })}
        <RiskHitSwitch />
        <span style={{ flex: 1 }} />
        <button
          style={styles.link}
          onClick={exitDevMode}
          title="Turn off dev mode and test cards in this browser"
        >
          exit dev mode
        </button>
      </div>
    </>
  );
}

/** Next forced `h` on click: real → 0 → 1 → real. */
const NEXT_FORCED_H = { real: 0, 0: 1, 1: undefined } as const;

/**
 * Forces the backend's risk answer `h` in this browser, so both checkout
 * variants can be seen without a matching session. Shows the backend's own
 * answer while not forced.
 */
function RiskHitSwitch() {
  const forced = useForcedH();
  const real = React.useSyncExternalStore(
    subscribeDevTools,
    () => browserLocalStorage.get().webSession?.h,
    () => undefined,
  );

  return (
    <button
      style={{ ...styles.link, opacity: forced === undefined ? 0.6 : 1 }}
      onClick={() => setForcedH(NEXT_FORCED_H[forced ?? "real"])}
      title="Risk answer h used by the checkout. Click to cycle: backend's answer → force 0 → force 1"
      data-testid="dev-toolbar-h"
    >
      {forced === undefined ? `h: real (${real ?? "?"})` : `h: ${forced} forced`}
    </button>
  );
}

const styles: Record<string, React.CSSProperties> = {
  bar: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    height: DEV_TOOLBAR_HEIGHT,
    boxSizing: "border-box",
    display: "flex",
    // One line that scrolls sideways on phones, so the height stays fixed and
    // panels above it (bottom: 40) never overlap.
    flexWrap: "nowrap",
    overflowX: "auto",
    alignItems: "center",
    columnGap: 14,
    padding: "0 10px",
    background: "#000",
    borderTop: `1px solid ${GREEN}`,
    color: GREEN,
    font: "11px/1.4 ui-monospace, SFMono-Regular, Menlo, monospace",
    zIndex: 2147483646,
  },
  link: {
    background: "none",
    border: "none",
    padding: 0,
    color: GREEN,
    font: "inherit",
    cursor: "pointer",
    whiteSpace: "pre",
  },
  tab: {
    position: "fixed",
    left: 8,
    bottom: 8,
    padding: "3px 8px",
    background: "#000",
    border: `1px solid ${GREEN}`,
    borderRadius: 4,
    font: "bold 11px ui-monospace, SFMono-Regular, Menlo, monospace",
    zIndex: 2147483646,
  },
};
